import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { MoreVerticalFill, MoveOutline } from '@lyearn/icons';
import { ActionMenu } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Stack } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';

import { useActionItemContext } from '../../context';
import useHovered from '../../hooks/useHovered';
import { ActionOptionsProps } from '../../types';
import { disabledButtonStyles } from '../ActionItem/styles';
import { ActionItemActions, getActionMenuItems } from './utils';

function ActionOptions({
  entityType,
  overrides,
  useMoreVertical = true,
  actionMenu,
}: ActionOptionsProps) {
  const { actionItem, actions, isReadOnly } = useActionItemContext();
  const { hovered, onMouseMove } = useHovered();

  const onAction = useCallback(
    (action: ActionItemActions) => {
      actions?.onMenuItemClick(action, {
        ...actionItem,
      });
    },
    [actionItem, actions],
  );
  const { t } = useTranslation();
  const Icon = useMoreVertical ? MoreVerticalFill : MoveOutline;

  const renderButton = () => (
    <LinkButton
      className={cn('py-2 px-4', 'action-menu-btn flex items-center', {
        [disabledButtonStyles]: isReadOnly,
      })}
      disabled={isReadOnly}>
      <Icon className="h-20 w-20 text-link-internal hover:text-link-internal-hover active:text-link-internal-active" />
    </LinkButton>
  );

  if (!hovered) {
    return <Stack onMouseMove={onMouseMove}>{renderButton()}</Stack>;
  }

  if (isReadOnly) {
    return renderButton();
  }

  return (
    <Box onClick={stopPropagation}>
      <ActionMenu<ActionItemActions>
        items={actionMenu ? actionMenu.items : getActionMenuItems(t, entityType)}
        minWidth={18}
        overrides={overrides}
        onAction={actionMenu ? actionMenu.onAction(actionItem) : onAction}>
        {renderButton()}
      </ActionMenu>
    </Box>
  );
}

export default React.memo(ActionOptions);
